import React, { useContext, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { LabeledValue } from 'antd/es/select'
import { useFormik } from 'formik'
import { useApiClient } from 'utils/api/ApiClient'
import { useFetchOne } from 'utils/api/useFetch'

import { ClientDetailedDto } from '../../../../api/Clients/Types/ClientDetailedDto'
import { PermissionsEnum } from '../../../../api/Identity/Types/PermissionsEnum'
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal'
import { Loading } from '../../../../components/Loading'
import { MultiSelect, Option } from '../../../../components/Select'
import { errorToast, successToast, ToastContext } from '../../../../components/Toast/Toast'
import { IdentityApiClient } from '../../../../utils/api/identityApi'
import { useCallbackWithForceRefresh } from '../../../../utils/api/useCallbackWithForceRefresh'
import { AuthSessionContext } from '../../../../utils/AuthContext'
import { hasPermission } from '../../../../utils/permissionsValidator'

interface Props {
  clientData: ClientDetailedDto
  updateClient(): void
  onClose(): void
}

export const ClientTwoFactorAuthMethodFormModal: React.FC<Props> = ({
  clientData,
  onClose,
  updateClient,
}) => {
  const identityApiClient = useApiClient(IdentityApiClient)
  const [confirmationModal, setConfirmationModal] = useState(false)
  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const { callback, forceRefresh } = useCallbackWithForceRefresh(async () => {
    const response = await identityApiClient.getClient2FASettings(clientData.id)
    return response.twoFactorAuthTypes
  }, [clientData.id])
  const { data = [], isLoading } = useFetchOne(callback)

  const formik = useFormik({
    initialValues: {
      authMethods: [] as LabeledValue[],
    },
    onSubmit: async (values) => {
      const updatedValues = values.authMethods.map((p) => {
        const found = data.find((item) => item.id === Number(p.key))
        return {
          authMedium: found?.authMedium ?? '',
          id: Number(p.key),
        }
      })
      const possibleDuplicatesPhones = updatedValues.filter((p) => p.id === 2)
      if (possibleDuplicatesPhones.length > 1) {
        setToast(errorToast(`Cannot enable two phone numbers. Please choose only one.`))
        return
      }
      await identityApiClient.updateClient2FASettings(clientData.id, updatedValues)
      setToast(successToast('2FA settings for client updated!'))
      updateClient()
      forceRefresh()
      onClose()
    },
  })

  useEffect(() => {
    if (data.length > 0) {
      const initialValues = {
        authMethods: data
          .filter((p) => p.isEnabled)
          .map((p) => ({
            label: `${p.name} ${p.authMedium ? p.authMedium : ''}`.trim(),
            key: p.id.toString(),
            value: p.id.toString(),
          })),
      }

      formik.resetForm({ values: initialValues })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const canEdit = hasPermission(permissions, PermissionsEnum.CanEditClientTwoFactorAuthentication)

  return (
    <>
      {confirmationModal && (
        <ConfirmationModal
          contentText='Are you sure you wish to change 2FA settings for the client?'
          yesButtonText='Yes'
          onYes={formik.handleSubmit}
          onCancel={() => setConfirmationModal(false)}
          visible={confirmationModal}
        />
      )}
      <Dialog open={!confirmationModal} onOpenChange={(open) => !open && onClose()}>
        <DialogContent size='lg'>
          <DialogHeader>
            <DialogTitle>2FA Settings</DialogTitle>
          </DialogHeader>

          <Loading isLoading={isLoading}>
            <MultiSelect
              labelInValue={true}
              placeholder='Select 2FA options'
              disabled={!canEdit}
              value={formik.values.authMethods}
              allowClear={true}
              size={'lg'}
              fullWidth
              onChange={(labeledValues: LabeledValue[]) => {
                const updatedValues = labeledValues.map((l) => {
                  const foundItem = data.find((item) => item.id.toString() === l.value?.toString())
                  if (foundItem) {
                    return {
                      label: `${foundItem.name} ${
                        foundItem.authMedium ? foundItem.authMedium : ''
                      }`.trim(),
                      key: foundItem.id.toString(),
                      value: foundItem.id.toString(),
                    }
                  }
                  return l
                })
                formik.setFieldValue('authMethods', updatedValues)
              }}
            >
              {(data || []).map((p) => (
                <Option disabled={!p.isAvailable} value={p.id.toString()} key={p.id}>
                  {`${p.name} ${p.authMedium ? p.authMedium : ''}`.trim()}
                </Option>
              ))}
            </MultiSelect>
          </Loading>

          <DialogFooter>
            <Button variant='outline' type='button' onClick={onClose}>
              Cancel
            </Button>
            <Button
              type='button'
              onClick={() => setConfirmationModal(true)}
              disabled={formik.isSubmitting || !canEdit}
            >
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
